import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Link2Off } from 'lucide-react';
import { Button, EmptyState, ErrorState, Modal, Skeleton, toast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney } from '../../lib/format';

interface CandidateLine {
  lineId: string;
  invoiceId: string;
  number: string;
  issueDate: string;
  state: string;
  description: string;
  amount: number;
  kind: string;
  agreement: { id: string; name: string; isActive: boolean } | null;
  suggested: boolean;
}

interface Candidates {
  window: { from: string; to: string } | null;
  all: boolean;
  truncated: boolean;
  lines: CandidateLine[];
}

/**
 * LINK PAST INVOICES TO THIS AGREEMENT — many at once.
 *
 * Jesse, 2026-11-04: *"I think I need a way to associate old invoices with
 * these projects."* Harvest booked 2026's monthly invoices against LAST year's
 * agreements, so "2026 Hosting" held only October and November. This lists the
 * client's other lines in the agreement's period, pre-ticks the ones on last
 * year's same-named agreement (`backend/.../agreement-lines.ts`), and moves the
 * ticked lines here in one go. It moves attribution, never money.
 */
export function LinkInvoicesModal({
  open,
  onOpenChange,
  agreementId,
  agreementName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agreementId: string;
  agreementName: string;
}) {
  const [all, setAll] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const candidates = useQuery({
    queryKey: ['invoices', 'agreement-lines', agreementId, all],
    queryFn: () =>
      api.get<Candidates>(
        `/invoices/agreement-lines?agreementId=${encodeURIComponent(agreementId)}${all ? '&all=1' : ''}`,
      ),
    enabled: open,
  });

  // Start from the suggestions each time a list arrives.
  useEffect(() => {
    if (candidates.data) {
      setPicked(new Set(candidates.data.lines.filter((l) => l.suggested).map((l) => l.lineId)));
    }
  }, [candidates.data]);

  useEffect(() => {
    if (!open) {
      setAll(false);
      setError(null);
    }
  }, [open]);

  const link = useMutation({
    mutationFn: (lineIds: string[]) =>
      api.post<{ linked: number; invoices: number }>('/invoices/agreement-lines', {
        agreementId,
        lineIds,
      }),
    onSuccess: () => {
      for (const key of ['invoices', 'agreements', 'support', 'clients']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  const lines = candidates.data?.lines ?? [];
  const suggested = lines.filter((l) => l.suggested);
  const chosen = lines.filter((l) => picked.has(l.lineId));
  const total = useMemo(
    () => chosen.reduce((s, l) => s + Math.round(l.amount * 100), 0) / 100,
    [chosen],
  );
  const window = candidates.data?.window;

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    setError(null);
    try {
      const r = await link.mutateAsync(chosen.map((l) => l.lineId));
      toast.success(
        `${r.linked} line${r.linked === 1 ? '' : 's'} on ${r.invoices} invoice${r.invoices === 1 ? '' : 's'} linked to ${agreementName}.`,
      );
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The lines could not be linked.');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Link invoices"
      description={
        window && !all
          ? `Lines on this client's invoices from ${formatDate(window.from)} to ${formatDate(window.to)} that are not on ${agreementName}. Suggested: the ones on last year's agreement of the same name.`
          : `Lines on this client's invoices that are not on ${agreementName}.`
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-body-sm">
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={suggested.length === 0}
              onClick={() => setPicked(new Set(suggested.map((l) => l.lineId)))}
            >
              Select suggested ({suggested.length})
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={picked.size === 0}
              onClick={() => setPicked(new Set())}
            >
              Clear
            </Button>
          </div>
          {window && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setAll((v) => !v)}>
              {all
                ? `Only ${window.from.slice(0, 4) === window.to.slice(0, 4) ? window.from.slice(0, 4) : 'this period'}`
                : 'Show every year'}
            </Button>
          )}
        </div>

        {candidates.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-11 rounded-lg" />
            ))}
          </div>
        ) : candidates.isError ? (
          <ErrorState
            title="Couldn't load the invoices"
            message={candidates.error instanceof Error ? candidates.error.message : undefined}
            onRetry={() => void candidates.refetch()}
          />
        ) : lines.length === 0 ? (
          <EmptyState
            title="Nothing to link"
            description={
              window && !all
                ? 'Every line in this period is already on this agreement.'
                : 'Every invoice line for this client is already on this agreement.'
            }
          />
        ) : (
          <ul className="divide-y divide-grey-100 rounded-lg border border-grey-200">
            {lines.map((l) => (
              <li key={l.lineId}>
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-3 px-3 py-2 text-body-sm hover:bg-grey-50',
                    picked.has(l.lineId) && 'bg-accent/5',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={picked.has(l.lineId)}
                    onChange={() => toggle(l.lineId)}
                    className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-grey-300 accent-accent"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="num font-semibold text-secondary">#{l.number}</span>
                      <span className="num text-grey-500">{formatDate(l.issueDate)}</span>
                      {l.suggested && (
                        <span className="rounded bg-accent/10 px-1.5 text-caption font-semibold text-accent">
                          suggested
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-grey-700">{l.description}</span>
                    <span className="flex items-center gap-1 text-caption text-grey-500">
                      {l.agreement ? (
                        <>
                          <Link2 className="h-3 w-3" aria-hidden />
                          now on {l.agreement.name}
                          {!l.agreement.isActive && ' (archived)'}
                        </>
                      ) : (
                        <>
                          <Link2Off className="h-3 w-3" aria-hidden />
                          on no agreement
                        </>
                      )}
                    </span>
                  </span>
                  <span className="num shrink-0 text-right text-secondary">
                    {formatMoney(l.amount)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {candidates.data?.truncated && (
          <p className="text-caption text-grey-500">Showing the 500 most recent lines.</p>
        )}

        {error && (
          <p className="text-caption text-status-late" role="alert">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-grey-100 pt-3">
          <span className="text-body-sm text-grey-600">
            {chosen.length > 0 ? (
              <>
                {chosen.length} line{chosen.length === 1 ? '' : 's'} ·{' '}
                <span className="num font-semibold text-secondary">
                  {formatMoney(total, { whole: true })}
                </span>
              </>
            ) : (
              'Nothing selected'
            )}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={chosen.length === 0}
              loading={link.isPending}
              onClick={() => void submit()}
            >
              Link {chosen.length || ''} to this agreement
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
